package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.api.IdeMetadata
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

class ComponentTokenSymbolPass(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        if (screen.dslHeader?.mode != DslMode.STRICT) return
        val tokens = screen.theme?.tokens?.keys.orEmpty()
        checkElement(screen.root, tokens)
    }

    private fun checkElement(element: ElementAst, tokens: Set<String>) {
        for (prop in element.props) {
            when (prop.name) {
                "variant" -> checkSymbolProp(element.kind, "variants", prop, tokens)
                "controlSize" -> checkSymbolProp(element.kind, "sizes", prop, tokens)
                "textStyle" -> checkTextStyle(prop, tokens)
            }
        }
        element.children.forEach { checkElement(it, tokens) }
        element.conditionalContent?.let { checkConditional(it, tokens) }
        element.forContent?.let { checkFor(it, tokens) }
    }

    private fun checkConditional(content: ConditionalContentAst, tokens: Set<String>) {
        content.branches.forEach { checkElement(it.body, tokens) }
        content.elseBranch?.let { checkElement(it, tokens) }
    }

    private fun checkFor(content: ForContentAst, tokens: Set<String>) {
        checkElement(content.body, tokens)
    }

    private fun checkSymbolProp(
        kind: String,
        group: String,
        prop: ElementAst.PropAssign,
        tokens: Set<String>,
    ) {
        for (symbol in symbols(prop.value)) {
            val prefix = "$kind.$group.$symbol."
            if (tokens.none { it.startsWith(prefix) }) {
                diag.error(DslError.AUI_V2_UNKNOWN_COMPONENT_TOKEN, prop.position, kind, group, symbol)
            }
        }
    }

    private fun checkTextStyle(prop: ElementAst.PropAssign, tokens: Set<String>) {
        for (symbol in symbols(prop.value)) {
            val prefix = "typography.$symbol."
            if (symbol !in IdeMetadata.textTypographyRoles && tokens.none { it.startsWith(prefix) }) {
                diag.error(DslError.AUI_V2_UNKNOWN_COMPONENT_TOKEN, prop.position, "text", "typography", symbol)
            }
        }
    }

    private fun symbols(expr: ExprAst): List<String> = when (expr) {
        is ExprAst.Literal.StringLit -> listOf(expr.value)
        is ExprAst.IfExpr -> symbolsFromBranch(expr.thenBody) + symbolsFromBranch(expr.elseBody)
        else -> emptyList()
    }

    private fun symbolsFromBranch(body: List<StmtAst>): List<String> =
        body.mapNotNull { stmt ->
            val expr = (stmt as? StmtAst.ExprStmt)?.call as? ExprAst.Literal.StringLit
            expr?.value
        }
}
